import { isUnansweredRequest } from '@flashka/shared/api';
import {
  type AccountSwitchListener,
  createAccountSwitchSignal,
  exposeRetryAfter,
  isAnswerableTokenTimeout,
  jwtLifetimeSeconds,
  onSocketCredentialRejected,
  resetQueryCacheForAccountSwitch,
  type ResettableQueryClient,
  SESSION_REFETCH_INTERVAL_S,
  TOKEN_HANG_MS,
  watchTokenHang,
} from '@flashka/shared/auth';

import * as Sentry from '@sentry/react';
import { emailOTPClient, jwtClient, usernameClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';

import { SESSION_TOKEN_KEY as TOKEN_KEY } from './impersonation-handoff';

/**
 * The Better Auth client behind `./session`. Evaluated at app boot, so
 * nothing here may reach the network at module scope.
 *
 * ONE transport, always: `/api/auth/*` on the app's OWN origin. The server
 * proxies every call to the auth worker (server/routes/auth-proxy.ts, which
 * the Vite dev server runs in-process too), so the session cookie lands
 * FIRST-PARTY and top-level navigations — the OAuth callback in particular —
 * are authenticated without any client involvement. The worker's own origin is
 * never shipped to the browser and never needs to be: to develop against a
 * locally-running auth worker, point the SERVER-side `AUTH_URL` at it
 * (`AUTH_URL=http://localhost:8790 pnpm dev`) and the proxy does the rest.
 * Calling the worker cross-origin is not an alternative — its OAuth leg needs
 * third-party cookies on the worker origin, which Firefox's Total Cookie
 * Protection (on by default) and Safari partition away.
 *
 * The Bearer attachment below is NOT a second transport. It is how a session
 * gets PLANTED into an app that is already running on this origin, and it has
 * exactly TWO writers — nothing else may ever store this key:
 *  - admin impersonation: the worker's Access-gated admin UI opens
 *    `/impersonate#token=…` here. The token is NOT trusted on sight — the
 *    handoff resolves it first and refuses anything that is not a genuine
 *    admin impersonation session, then a human confirms the named account
 *    before it is written (impersonation-handoff.ts owns the key — it must be
 *    able to write it without evaluating this module —, and
 *    components/impersonate-confirm.tsx owns the gate).
 *  - e2e: the suite seeds that same key to reuse a saved session.
 * A planted token OUTRANKS whatever first-party cookie the browser holds: the
 * proxy forwards `Authorization` untouched and the worker's bearer plugin
 * rewrites the request's session cookie from it. Normal sign-ins do NOT go
 * through here at all — they ride the first-party cookie the proxy sets.
 *
 * That precedence is why this client must NEVER capture the `set-auth-token`
 * response header a sign-in returns (it used to, back when a direct
 * cross-origin `VITE_AUTH_URL` mode existed and the Bearer WAS the transport).
 * Capturing it means every sign-in leaves a long-lived token behind; the moment
 * that token's session dies — expiry, sign-out in another tab, a DB reset — the
 * dead Bearer permanently SHADOWS the cookie, because the worker rewrites the
 * cookie from it. A fresh Google OAuth then mints a perfectly good first-party
 * cookie and `get-session` still resolves the corpse, so the user is signed out
 * forever with no way back short of clearing localStorage.
 */
export const authClient = createAuthClient({
  plugins: [jwtClient(), emailOTPClient(), usernameClient()],
  // Bounds how long a tab revoked from ANOTHER device keeps running as
  // signed-in while it stays focused (see the constant's doc).
  sessionOptions: { refetchInterval: SESSION_REFETCH_INTERVAL_S },
  fetchOptions: {
    // These are same-origin requests, so the first-party session cookie would
    // ride along under fetch's default policy anyway — stated explicitly so a
    // change of that default can never silently drop the session.
    credentials: 'include',
    // A 429's wait rides in its X-Retry-After header, which the `{ error }`
    // result drops; this copies it into the error so `waitOutRateLimit`
    // (useAuthAction) can count it down instead of failing the sign-in.
    onResponse: exposeRetryAfter,
    auth: {
      type: 'Bearer',
      token: () => localStorage.getItem(TOKEN_KEY) ?? '',
    },
    // Better Auth folds this into an internal fetch PLUGIN
    // (client/config.ts `lifeCyclePlugin`), so unlike a plain @better-fetch
    // `fetchOptions.onSuccess` it is never shadowed by a per-call hook — it
    // runs for every successful auth response, the session atom's own
    // `/get-session` included. That is the only reason the heal below can be
    // hung here rather than on `onResponse`, where the body isn't parsed yet.
    onSuccess(ctx) {
      // Spends nothing; just drops the one-shot reload flag on the first auth
      // response of this page load (≈ boot). Memoized, so the flag can only
      // ever describe the reload that produced THIS page.
      consumeHealReloadFlag();
      if (
        shouldClearStaleToken({
          url: ctx.request.url,
          data: ctx.data,
          // Reads the CURRENTLY stored token rather than the one this request
          // actually carried. The race is real but harmless in both
          // directions: a token planted mid-flight only costs one wasted heal
          // of a token that was about to be revalidated anyway, and a token
          // cleared mid-flight (sign-out) suppresses a heal on a page that is
          // already navigating away (session.tsx clears then assigns '/').
          hadToken: getSessionToken() !== null,
        })
      ) {
        healStaleSessionToken();
      }
    },
  },
});

/**
 * Where `setProfileImage` POSTs — relative, so the app's OWN origin, exactly
 * like `authClient` above. `server/routes/auth-avatar.ts` forwards the upload
 * (and the caller's credentials) to the worker. The worker's origin is not
 * knowable in the browser and the session cookie is first-party ONLY here, so
 * a direct call could neither be addressed nor authenticated.
 *
 * Note `/api/avatar` is deliberately NOT under `/api/auth`: that prefix is the
 * Better Auth path space the proxy maps 1:1, and the worker serves avatars
 * outside it.
 */
export const AVATAR_ENDPOINT = '/api/avatar';

/**
 * The Better Auth SESSION token (not the 15-min app JWT below) — the same
 * localStorage value `authClient`'s `fetchOptions.auth` sends, so a hand-rolled
 * call (uploadAvatar in ./session) authenticates identically to a client-SDK
 * one whenever a session has been planted.
 */
export function getSessionToken(): string | null {
  return localStorage.getItem(TOKEN_KEY) || null;
}

export function clearSessionToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  clearJwtCache();
}

/**
 * The ONE thing every sign-in / sign-up / e-mail-verification entry point must
 * do FIRST: drop every credential this tab holds locally — the planted Bearer
 * session token AND the cached 15-min JWT.
 *
 * It has its own name (rather than each caller remembering the pair) because
 * clearing only the JWT cache is silently insufficient, and that is exactly
 * what five of the five credential flows used to do. A token already sitting in
 * localStorage is attached as `Authorization: Bearer` on every auth request
 * (see `authClient`'s `fetchOptions.auth` above), the same-origin proxy
 * forwards it untouched, and the worker's bearer plugin rewrites the request's
 * session cookie FROM it — a live foreign token is never stripped. So the
 * correct first-party cookie a fresh sign-in just minted is immediately
 * outranked by the old token, and the user, believing they signed in as
 * themselves, operates as whoever owns it.
 *
 * Every new sign-in path must route through here; nothing else needs to know
 * which two pieces of state exist.
 */
export function resetLocalCredentials(): void {
  // Deliberately NOT `clearSessionToken(); clearJwtCache();` — the former
  // already drops the cache, and spelling both out would imply it doesn't.
  clearSessionToken();
}

// ── self-heal for browsers poisoned before the capture was removed ──────────
//
// Every sign-in used to leave a `set-auth-token` behind (see the note on
// `authClient`), so real users are still walking around with a dead token in
// localStorage that shadows their cookie session for good. The heal below is
// what gets them back in without a support ticket: a `/get-session` that was
// sent WITH a stored token and came back WITHOUT a session can only mean the
// token is dead (a live one always resolves), so drop it and reload once so the
// first-party cookie — if the browser holds one — takes over.
//
// It is deliberately blind to WHERE the token came from, which is the right
// behaviour for the impersonation handoff too: a freshly planted token resolves
// a session, so the heal never fires while the impersonation is live; once that
// session is revoked the heal clears the corpse, which is exactly what should
// happen.

/** sessionStorage flag whose ONLY job is to survive the heal's own reload. */
const STALE_TOKEN_RELOAD_KEY = 'flashka.ba.stale-token-reload';

/** `null` until the flag has been read once on this page load. */
let reloadedByHeal: boolean | null = null;

/**
 * Reads — and immediately drops — the reload flag, memoizing the answer for the
 * rest of this page load. Dropping it is what keeps the budget per-page-load
 * instead of per-tab; the in-memory copy is what makes a second reload from
 * THIS page impossible.
 */
function consumeHealReloadFlag(): boolean {
  if (reloadedByHeal === null) {
    reloadedByHeal = sessionStorage.getItem(STALE_TOKEN_RELOAD_KEY) !== null;
    sessionStorage.removeItem(STALE_TOKEN_RELOAD_KEY);
  }
  return reloadedByHeal;
}

/**
 * The decision half of the heal, kept pure so it can be unit-tested without a
 * Better Auth client: was this response a `/get-session` that carried a token
 * and resolved nobody?
 *
 * `data` is what @better-fetch already parsed for us (`ctx.data`) — a
 * signed-out `/get-session` answers 200 with a `null` body, and the client's
 * jsonParser turns an empty body into `null` too, so anything without a truthy
 * `user` counts as "no session".
 */
export function shouldClearStaleToken(args: {
  url: URL | string;
  data: unknown;
  hadToken: boolean;
}): boolean {
  // No token attached ⇒ nothing could have been shadowed; a signed-out visitor
  // polling /get-session is not a bug.
  if (!args.hadToken) return false;
  const { url } = args;
  // Relative baseURL ⇒ @better-fetch hands us a string like
  // `/api/auth/get-session?disableCookieCache=true`; an absolute one would give
  // a URL. Handle both, and only ever look at the path.
  const path = (typeof url === 'string' ? url : url.pathname).split('?')[0] ?? '';
  if (!path.endsWith('/get-session')) return false;
  const data = args.data as { user?: unknown } | null | undefined;
  return !(typeof data === 'object' && data !== null && Boolean(data.user));
}

/**
 * The impure half: drop the poisoned token and reload ONCE so the cookie
 * session gets a turn. `reload` is injectable purely so the loop guard is
 * testable — jsdom has no navigation.
 *
 * Loop safety, twice over: after the clear no token is attached to anything, so
 * the reloaded page can't satisfy `shouldClearStaleToken` at all; and even if it
 * somehow did (a token re-planted by the e2e suite, say) the flag this sets is
 * still on the reloaded page, so the reload is skipped.
 */
export function healStaleSessionToken(
  reload: () => void = () => window.location.reload(),
): void {
  const alreadyReloaded = consumeHealReloadFlag();
  clearSessionToken();
  if (alreadyReloaded) return;
  reloadedByHeal = true;
  sessionStorage.setItem(STALE_TOKEN_RELOAD_KEY, '1');
  reload();
}

/**
 * Drop the cached JWT — plus the failure cooldown, so a fresh session is not
 * held unauthenticated by a previous session's failure.
 *
 * Still called explicitly by every sign-in / sign-up / verification entry point
 * (through `resetLocalCredentials`) and by `reload()` in ./session, but the
 * session-atom subscription at the bottom of this file is what makes the cache
 * correct for session transitions no local code path sees at all — a session
 * revoked from another device or another tab.
 */
export function clearJwtCache(): void {
  jwtCache = null;
  inflight = null;
  cooldownUntil = 0;
  // Invalidates every /token round-trip currently in the air — see `generation`.
  generation++;
}

// 15-min EdDSA JWTs from the /token endpoint, cached until shortly before
// expiry, with in-flight dedupe so a burst of concurrent callers shares one
// /token round-trip. Backs `useAuth().getToken()`, with the same contract as
// flashka-app's `auth/better/jwt.ts`: resolves null on failure,
// never rejects, bounded by a timeout — every caller (notably the ambient
// fetch wrapper, which awaits this inside window.fetch) has to be able to
// proceed unauthenticated instead of inheriting a token error.
let jwtCache: { token: string; exp: number } | null = null;
let inflight: Promise<string | null> | null = null;
let cooldownUntil = 0;

// Bumped by every `clearJwtCache()`. A /token request captures the generation
// when it starts and only writes module state back if it still matches, so a
// round-trip that was already in the air when the cache was cleared can neither
// re-install a dead session's JWT, nor arm the failure cooldown against the new
// session, nor null out a NEWER caller's in-flight promise (which would
// silently defeat the single-flight dedupe).
let generation = 0;

// The user id the cached token belongs to. `null` doubles as "signed out",
// which is the right starting value: the first session emission for a
// signed-in user then registers as a change and clears a cache that is empty
// anyway.
let cacheUserId: string | null = null;

/**
 * Re-key the cache to `userId`, dropping the cached token whenever the
 * signed-in user changes — a fresh sign-in, a session that ended, or a session
 * revoked elsewhere followed by somebody else signing in on this tab.
 *
 * Without this key the cache is a bare module singleton: a tab whose session
 * was revoked (account security's "log out other devices", a sign-out in
 * ANOTHER tab, an admin impersonation that ended) keeps handing out a working
 * 15-min Bearer for the rest of that token's TTL, because no local code path
 * runs at all when the revocation happens somewhere else. Ported from
 * flashka-app's `auth/better/jwt.ts` (`setJwtCacheUser`).
 */
export function setJwtCacheUser(userId: string | null): void {
  if (userId === cacheUserId) return;
  cacheUserId = userId;
  clearJwtCache();
}

// Declared above `watchSessionUser`'s call at the bottom of this file, for the
// same reason as the JWT state: the subscription fires synchronously.
const accountSwitches = createAccountSwitchSignal();

/**
 * Run `listener` whenever a DIFFERENT account signs in on this tab without a
 * reload — see `@flashka/shared/auth/account-switch`. It runs inside the
 * session-atom notification, before React renders the new user, and after the
 * JWT cache has been dropped, so a refetch it starts already authenticates as
 * the new account.
 */
export function onAccountSwitch(listener: AccountSwitchListener): () => void {
  return accountSwitches.subscribe(listener);
}

/**
 * The app's react-query reset on every account switch — installed once, at
 * module scope, by App.tsx, and by the spec that pins it.
 */
export function installAccountSwitchReset(
  queryClient: ResettableQueryClient,
): () => void {
  return onAccountSwitch(() => resetQueryCacheForAccountSwitch(queryClient));
}

/**
 * better-auth's `session` atom is the one place EVERY session transition shows
 * up: `signIn.*`, `signUp.email`, `verifyEmail`, `signOut`, `revokeSession` /
 * `revokeOtherSessions`, the cross-tab broadcast, and the plain refetch that
 * notices a session revoked from another device.
 *
 * `$store.atoms` is typed `Record<string, WritableAtom<any>>`, so the shape is
 * narrowed structurally here rather than trusted.
 */
function watchSessionUser(atoms: Record<string, unknown>): void {
  const sessionAtom = atoms.session as
    | { subscribe: (listener: (value: unknown) => void) => unknown }
    | undefined;
  // nanostores `subscribe` fires immediately with the current value and again
  // on every change; `setJwtCacheUser` no-ops unless the user id actually
  // moved, so the refetch/isRefetching churn costs nothing. JWT first: an
  // account-switch subscriber may refetch at once.
  sessionAtom?.subscribe((value) => {
    setJwtCacheUser(readSessionUserId(value));
    accountSwitches.observe(value);
  });
}

/** The fields of better-auth's `session` atom that ./client reads. */
type SessionAtomValue = {
  data?: { user?: { id?: unknown } } | null;
  error?: { status?: unknown } | null;
  isRefetching?: boolean;
  refetch?: (params?: { query?: Record<string, unknown> }) => Promise<unknown>;
};
type SessionAtom = { get: () => SessionAtomValue };

/** `null` for a signed-out (or not-yet-loaded) session atom value. */
function readSessionUserId(value: unknown): string | null {
  const id = (value as { data?: { user?: { id?: unknown } } | null } | null)?.data?.user
    ?.id;
  return typeof id === 'string' ? id : null;
}

const FALLBACK_TTL_S = 10 * 60;
const TOKEN_TIMEOUT_MS = 10_000;
const FAILURE_COOLDOWN_MS = 5_000;

function withTimeout<T>(p: Promise<T>, fallback: T, onTimeout?: () => void): Promise<T> {
  let handle: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<T>((resolve) => {
    handle = setTimeout(() => {
      onTimeout?.();
      resolve(fallback);
    }, TOKEN_TIMEOUT_MS);
  });
  return Promise.race([p, timeout]).finally(() => clearTimeout(handle));
}

/** One event per failure per page load: every later call fails the same way. */
const reportedTokenFailures = new Set<string>();

/**
 * A missing token is every later RPC and sync socket going out without a
 * Bearer, so the failures that point at OUR side are reported — once each, as
 * flashka-app's `auth/better/jwt.ts` does: a 5xx (a broken auth deploy), a
 * signed-in session refused or answered without a token, a hang, an
 * unclassified throw. A signed-out visitor's refusal and an offline browser
 * are the expected answers, and stay silent.
 */
function reportTokenFailureOnce(
  key: string,
  err: Error,
  status?: number,
  context?: { tags: Record<string, string>; extra: Record<string, unknown> },
): void {
  if (reportedTokenFailures.has(key)) return;
  reportedTokenFailures.add(key);
  Sentry.captureException(err, {
    level: status != null && status >= 500 ? 'error' : 'warning',
    tags: {
      auth_stage: 'token',
      ...(status != null ? { http_status: String(status) } : {}),
      ...context?.tags,
    },
    ...(context ? { extra: context.extra } : {}),
  });
}

function pageVisible(): boolean {
  return document.visibilityState === 'visible';
}

/**
 * What a timeout report needs to tell a slow auth worker from a tab that could
 * not have been answered: hidden (a frozen or throttled tab) or offline. The
 * first 20 timeouts (FLASHKA-VITE-2XX, 2026-10-19) carried neither, alongside
 * the sync DOs' own `JWKSTimeout` against the same worker.
 */
export function tokenTimeoutContext(wait?: {
  visibleAtStart: boolean;
  elapsedMs: number;
}): {
  tags: Record<string, string>;
  extra: Record<string, unknown>;
} {
  return {
    tags: {
      page_visible: String(pageVisible()),
      online: String(navigator.onLine),
    },
    extra: { pageAgeMs: Math.round(performance.now()), ...wait },
  };
}

/**
 * The timer beat the mint: callers go on without a Bearer, and this breadcrumb
 * explains why. It is not an event — the request is still in the air and a
 * token that lands late is kept, so a slow answer is not a failure
 * (FLASHKA-VITE-2XX: ~2k users a week, the one inspected answered at ~12 s).
 * `watchTokenHang` reports the mint only if nothing comes back at all.
 */
function noteTokenTimeout(startedAtMs: number, visibleAtStart: boolean): void {
  const context = tokenTimeoutContext({
    visibleAtStart,
    elapsedMs: Date.now() - startedAtMs,
  });
  Sentry.addBreadcrumb({
    category: 'auth.token',
    level: 'warning',
    message: 'token unavailable: timeout',
    data: { ...context.tags, ...context.extra },
  });
}

/**
 * Still unanswered at `TOKEN_HANG_MS`: the auth worker hung — unless the tab
 * could not have been answered (hidden, offline, frozen timers: FLASHKA-VITE
 * 7800902876, `isAnswerableTokenTimeout`).
 */
function noteTokenHang(elapsedMs: number, visibleAtStart: boolean): void {
  if (
    !isAnswerableTokenTimeout({
      foregroundAtStart: visibleAtStart,
      foregroundAtTimeout: pageVisible(),
      online: navigator.onLine,
      elapsedMs,
      timeoutMs: TOKEN_HANG_MS,
    })
  ) {
    return;
  }
  reportTokenFailureOnce(
    'timeout',
    new Error(`Auth token request unanswered after ${TOKEN_HANG_MS}ms`),
    undefined,
    tokenTimeoutContext({ visibleAtStart, elapsedMs }),
  );
}

/**
 * A 401 while the session atom still holds a user is either a session that
 * ended on the server (expired overnight in an open tab — FLASHKA-VITE-2YK /
 * -2YE) or a Bearer the server will not mint for a live session, which is ours
 * to fix. Ask the server which, through the session ATOM's own refetch: it
 * writes the answer back to the store, so an ended session signs the tab out
 * (`watchSessionUser`) as it would on its own. A bare `getSession()` is a plain
 * fetch that never touches the store, which left the tab signed in and asking
 * again on every refused token.
 */
function confirmRefusedSession(status: number): void {
  const sessionAtom = authClient.$store.atoms.session as SessionAtom | undefined;
  const refetch = sessionAtom?.get().refetch;
  if (!sessionAtom || !refetch) return;
  void refetch({ query: { disableCookieCache: true } })
    .then(() => {
      const value = sessionAtom.get();
      // Unanswered (offline, aborted by a newer refetch): says nothing either way.
      if (value.isRefetching || (value.error && value.error.status !== 401)) return;
      const live = readSessionUserId(value) != null;
      Sentry.addBreadcrumb({
        category: 'auth.token',
        level: 'warning',
        message: `token refused (${status})`,
        data: { sessionStillLive: live },
      });
      if (!live) return;
      reportTokenFailureOnce(
        `rejected:${status}`,
        new Error(`Auth token endpoint refused a signed-in session (${status})`),
        status,
      );
    })
    // The check itself not being answered says nothing either way.
    .catch(() => undefined);
}

async function requestToken(): Promise<string | null> {
  try {
    const { data, error } = (await authClient.token()) as {
      data?: { token?: unknown } | null;
      error?: { status?: unknown } | null;
    };
    if (typeof data?.token === 'string' && data.token) return data.token;
    const status = typeof error?.status === 'number' ? error.status : undefined;
    if (status != null && status >= 500) {
      reportTokenFailureOnce(
        `status:${status}`,
        new Error(`Auth token endpoint failed with status ${status}`),
        status,
      );
    } else if (cacheUserId != null) {
      if (status === 401) {
        confirmRefusedSession(status);
      } else {
        reportTokenFailureOnce(
          status != null ? `rejected:${status}` : 'no_token',
          new Error(
            status != null
              ? `Auth token endpoint refused a signed-in session (${status})`
              : 'Auth token endpoint answered a signed-in session without a token',
          ),
          status,
        );
      }
    }
    return null;
  } catch (err) {
    if (!isUnansweredRequest(err)) {
      reportTokenFailureOnce(
        'error',
        err instanceof Error ? err : new Error(`token fetch failed: ${String(err)}`),
      );
    }
    return null;
  }
}

/** The cached token while it has more than a minute left, else null. */
function liveCachedToken(): string | null {
  return jwtCache && jwtCache.exp - 60 > Date.now() / 1000 ? jwtCache.token : null;
}

export async function getJwt(): Promise<string | null> {
  const cached = liveCachedToken();
  if (cached) return cached;
  // Short negative cache: with the auth worker unreachable (or the visitor
  // signed out) every same-origin /api call would otherwise re-run the round
  // trip that just came back empty. A sign-in clears it via clearJwtCache().
  if (Date.now() < cooldownUntil) return null;
  inflight ??= (async () => {
    const startedAt = generation;
    // Before the request: the lifetime then also absorbs the round-trip.
    const startedAtMs = Date.now();
    const visibleAtStart = pageVisible();
    // A clear that landed mid-flight (sign-out, session swap, a revocation
    // the session atom just noticed) belongs to a session this token may no
    // longer match: hand it to the caller that asked for it BEFORE the clear
    // — its request was already authorized — but never seed the cache.
    // `exp` is LOCAL: the token's server-side lifetime counted on this
    // browser's clock, so a clock minutes off never keeps a dead token.
    const seedCache = (token: string) => {
      if (startedAt !== generation) return;
      jwtCache = {
        token,
        exp: startedAtMs / 1000 + jwtLifetimeSeconds(token, FALLBACK_TTL_S),
      };
    };
    const request = requestToken();
    let timedOut = false;
    try {
      const token = await withTimeout(request, null, () => {
        timedOut = true;
        noteTokenTimeout(startedAtMs, visibleAtStart);
        watchTokenHang(request, {
          startedAtMs,
          onHang: (elapsedMs) => noteTokenHang(elapsedMs, visibleAtStart),
          onSettled: (elapsedMs) =>
            Sentry.addBreadcrumb({
              category: 'auth.token',
              message: 'token answered late',
              data: { elapsedMs },
            }),
        });
      });
      if (!token) {
        // The timer won, but the request is still in the air: a token that
        // lands late is still this session's, so keep it for the next caller
        // instead of paying another round trip. The cooldown below is lifted
        // too: it describes a failure that no longer holds, and must not keep
        // the next mint back if this token is dropped again (a socket 4401).
        if (timedOut) {
          void request.then((late) => {
            if (!late || startedAt !== generation || liveCachedToken()) return;
            seedCache(late);
            cooldownUntil = 0;
          });
        }
        // A failure that belongs to the PREVIOUS session must not hold the new
        // one unauthenticated — that is exactly what clearJwtCache() resets.
        if (startedAt === generation) cooldownUntil = Date.now() + FAILURE_COOLDOWN_MS;
        return null;
      }
      seedCache(token);
      return token;
    } finally {
      // Only release the slot this request actually owns. After a clear,
      // `inflight` is either null or a NEWER request that must survive.
      if (startedAt === generation) inflight = null;
    }
  })();
  return inflight;
}

// Keyed last, deliberately: `subscribe` fires synchronously with the atom's
// current value, so every `let` it reaches has to be initialised by now.
//
// This is also what MOUNTS the session atom (nanostores `onMount`), which the
// SessionProvider would do on the next tick anyway. It costs no extra traffic:
// the atom's own boot `/get-session` is deferred to a macrotask (so nothing
// here reaches the network at module scope, as the note on `authClient` above
// requires) and deduped against the provider's, and better-auth only polls on
// `refetchInterval` while a session actually exists.
watchSessionUser(authClient.$store.atoms);

// The sync socket was refused the token it carried (a 4401 / 4403): drop it so
// the reconnect mints a fresh one, instead of re-sending this one until its
// TTL ends — three refusals, and sync halts. Not a session change: an
// in-flight mint stays valid. `null` (the socket's token is unknown) drops
// whatever is cached.
onSocketCredentialRejected((refused) => {
  if (!refused || jwtCache?.token === refused) jwtCache = null;
});
